import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import { useIsDesktop } from '@/hooks/use-user-platform';
import useUpdateNickName from '@/hooks/users/use-update-nickname';
import useUserInfo from '@/hooks/users/use-user-info';
import { nicknameSchema } from '@/utils/schemas';

export default function MyNicknameRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: updateNickname, isPending } = useUpdateNickName();
  const {
    control,
    watch,
    handleSubmit,
    resetField,
    formState: { isDirty },
  } = useForm<UpdateNicknameFormData>({
    resolver: zodResolver(updateNicknameFormScheme),
    defaultValues: { nickname: '' },
  });
  const nickname = watch('nickname');

  useAdjustLayout();

  useEffect(() => {
    if (userInfo?.NickName) {
      resetField('nickname', { defaultValue: userInfo.NickName });
    }
  }, [userInfo?.NickName, resetField]);

  const handleUpdateNickname = async (data: UpdateNicknameFormData) => {
    await updateNickname(data.nickname);
    navigate(-1);
  };

  if (isDesktop) return null;

  return (
    <PageLayout
      title={t('personal-info.edit-nickname')}
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        className='flex flex-1 flex-col justify-between mx-5 mt-4 mb-safe-offset-4'
        onSubmit={handleSubmit(handleUpdateNickname)}>
        <label
          htmlFor='nickname'
          className='form-control w-full'>
          <FormInput
            showClearButton={!!nickname.length}
            control={control}
            name='nickname'
            defaultValue=''
            maxLength={15}
            helperText={t('fields.messages.max-length', { name: t('personal-info.nickname'), count: 15 })}
          />
        </label>

        <ProgressButton
          as='submit'
          disabled={!isDirty}
          isLoading={isPending}
          className='btn btn-primary'
          label='common.confirm'
        />
      </form>
    </PageLayout>
  );
}

const updateNicknameFormScheme = z.object({
  nickname: nicknameSchema,
});

type UpdateNicknameFormData = z.infer<typeof updateNicknameFormScheme>;
